type LaneState = {
  lastEffectiveStart: number;
};

export type LaneAllocation = {
  lane: number;
  delayMs: number;
};

type LaneAllocatorOptions = {
  cooldownMs: number;
  neighborStaggerMs?: number;
  neighborStaggerWindowMs?: number;
  random?: () => number;
};

export class LaneAllocator {
  private lanes: LaneState[] = [];

  private readonly cooldownMs: number;

  private readonly neighborStaggerMs: number;

  private readonly neighborStaggerWindowMs: number;

  private readonly random: () => number;

  constructor({
    cooldownMs,
    neighborStaggerMs = 0,
    neighborStaggerWindowMs = 0,
    random = Math.random,
  }: LaneAllocatorOptions) {
    this.cooldownMs = cooldownMs;
    this.neighborStaggerMs = neighborStaggerMs;
    this.neighborStaggerWindowMs = neighborStaggerWindowMs;
    this.random = random;
  }

  setLaneCount(count: number): void {
    const clamped = Math.max(1, count);

    if (clamped === this.lanes.length) {
      return;
    }

    this.lanes = Array.from({ length: clamped }, () => ({
      lastEffectiveStart: -Infinity,
    }));
  }

  tryAllocate(now: number): LaneAllocation | null {
    if (this.lanes.length === 0) {
      this.setLaneCount(1);
    }

    const eligible: number[] = [];
    for (let i = 0; i < this.lanes.length; i++) {
      if (now - this.lanes[i].lastEffectiveStart >= this.cooldownMs) {
        eligible.push(i);
      }
    }

    if (eligible.length === 0) {
      return null;
    }

    const chosen = eligible[Math.floor(this.random() * eligible.length)];
    const delayMs = this.computeNeighborStagger(chosen, now);

    this.lanes[chosen].lastEffectiveStart = now + delayMs;

    return { lane: chosen, delayMs };
  }

  nextEligibleAt(): number {
    if (this.lanes.length === 0) {
      return -Infinity;
    }
    let earliest = Infinity;
    for (const lane of this.lanes) {
      const eligibleAt = lane.lastEffectiveStart + this.cooldownMs;
      if (eligibleAt < earliest) {
        earliest = eligibleAt;
      }
    }
    return earliest;
  }

  private computeNeighborStagger(lane: number, now: number): number {
    if (this.neighborStaggerMs <= 0 || this.neighborStaggerWindowMs <= 0) {
      return 0;
    }

    let latestNeighborStart = -Infinity;
    for (const offset of [-1, 1]) {
      const neighbor = lane + offset;
      if (neighbor >= 0 && neighbor < this.lanes.length) {
        const { lastEffectiveStart } = this.lanes[neighbor];
        if (now - lastEffectiveStart < this.neighborStaggerWindowMs) {
          latestNeighborStart = Math.max(latestNeighborStart, lastEffectiveStart);
        }
      }
    }

    if (latestNeighborStart === -Infinity) {
      return 0;
    }

    const proposed = latestNeighborStart + this.neighborStaggerMs - now;
    if (proposed <= 0) {
      return 0;
    }
    return proposed;
  }
}
